<div class="ticket-detail-page">

  <!-- Back button -->
  <button class="btn-back"
    type="button"
    (click)="goBack()">
    <mat-icon>arrow_back</mat-icon>
    {{ backLabel }}
  </button>

  <div *ngIf="loading" class="loading-state">
    <app-data-loader [show]="true"
      message="Loading ticket..."
      size="medium" />
  </div>

  <p class="help-form-error" *ngIf="actionError && !ticket">
    {{ actionError }}
  </p>

  <div *ngIf="ticket && !loading">

    <!-- Ticket header -->
    <div class="ticket-detail-header">
      <div class="ticket-detail-header__left">
        <div class="ticket-number-large">
          {{ ticket.ticket_number }}
        </div>
        <h1 class="ticket-detail-title">
          {{ ticket.subject }}
        </h1>
        <div class="ticket-detail-meta">
          <span [class]="'ticket-status ticket-status--' + ticket.status">
            {{ getStatusLabel(ticket.status) }}
          </span>
          <span [class]="'ticket-priority ticket-priority--' + ticket.priority">
            {{ ticket.priority | titlecase }} Priority
          </span>
          <span class="ticket-category-badge">
            {{ getCategoryLabel(ticket.category) }}
          </span>
          <span class="ticket-date-meta">
            Created {{ ticket.created_at | date:'MMM d, y h:mm a' }}
          </span>
        </div>
        <div class="assignment-flag"
          *ngIf="hasAssignment"
          [class.assignment-flag--station]="assignmentType === 'station'"
          [class.assignment-flag--admin]="assignmentType === 'admin'">
          <div class="assignment-flag__row">
            <span class="assignment-flag__badge">
              {{ assignmentType === 'admin'
                ? 'Assigned to admin'
                : assignmentType === 'station'
                  ? 'Assigned to station'
                  : 'Assignment' }}
            </span>
            <strong *ngIf="assignmentLabel">{{ assignmentLabel }}</strong>
          </div>
          <div class="assignment-flag__meta" *ngIf="reassignedByName || reassignedAt">
            Reassigned
            <ng-container *ngIf="reassignedByName"> by {{ reassignedByName }}</ng-container>
            <ng-container *ngIf="reassignedAt">
              on {{ reassignedAt | date:'MMM d, y h:mm a' }}
            </ng-container>
          </div>
        </div>
      </div>

      <!-- Admin/staff actions -->
      <div class="ticket-detail-actions"
        *ngIf="isStaff">

        <!-- Status update dropdown -->
        <mat-form-field
          appearance="outline"
          class="support-select support-select--sm"
          subscriptSizing="dynamic"
          *ngIf="ticket.status !== 'closed'">
          <mat-select
            [(ngModel)]="updateStatus"
            name="updateStatus"
            placeholder="Update Status"
            (selectionChange)="updateTicketStatus($event.value)"
            panelClass="owner-station-filter-panel">
            <mat-option value="in_progress">Mark In Progress</mat-option>
            <mat-option value="resolved">Mark Resolved</mat-option>
            <mat-option value="closed">Close Ticket</mat-option>
          </mat-select>
        </mat-form-field>

        <!-- Reassign (admin only) -->
        <button class="btn-secondary btn-sm btn-reassign"
          type="button"
          *ngIf="canReassign"
          (click)="openReassignPanel()">
          <mat-icon>swap_horiz</mat-icon>
          Reassign
        </button>
      </div>
    </div>

    <div class="reassign-overlay"
      *ngIf="showReassignPanel"
      (click)="closeReassignPanel()">
    </div>

    <div class="reassign-panel"
      [class.reassign-panel--open]="showReassignPanel">

      <div class="reassign-panel__header">
        <h3>Reassign Ticket</h3>
        <button class="reassign-panel__close"
          type="button"
          (click)="closeReassignPanel()">
          <svg width="18" height="18"
            viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="reassign-panel__body">
        <div class="reassign-ticket-info">
          <div class="reassign-ticket-num">
            {{ ticket.ticket_number }}
          </div>
          <div class="reassign-ticket-subject">
            {{ ticket.subject }}
          </div>
          <div class="reassign-current" *ngIf="hasAssignment">
            <span class="reassign-current__label">Currently assigned</span>
            <span class="reassign-current__value">
              {{ assignmentLabel || (assignmentType === 'admin' ? 'Admin' : 'Station') }}
            </span>
            <span class="reassign-current__meta" *ngIf="reassignedByName || reassignedAt">
              Reassigned
              <ng-container *ngIf="reassignedByName"> by {{ reassignedByName }}</ng-container>
              <ng-container *ngIf="reassignedAt">
                on {{ reassignedAt | date:'MMM d, y h:mm a' }}
              </ng-container>
            </span>
          </div>
        </div>

        <div class="reassign-loading"
          *ngIf="loadingReassignOptions">
          <app-data-loader [show]="true"
            message="Loading options..."
            size="small" />
        </div>

        <ng-container *ngIf="!loadingReassignOptions && reassignOptions">
          <div class="reassign-mode-toggle">
            <button
              type="button"
              [class.active]="reassignMode==='station'"
              (click)="reassignMode='station'; selectedUserId=null">
              Reassign to Station
            </button>
            <button
              type="button"
              [class.active]="reassignMode==='user'"
              (click)="reassignMode='user'; selectedStationId=null">
              Assign to Admin User
            </button>
          </div>

          <div *ngIf="reassignMode === 'station'">
            <label class="form-label">
              Select Station *
            </label>
            <mat-form-field appearance="outline"
              class="support-select"
              subscriptSizing="dynamic">
              <mat-select
                [(ngModel)]="selectedStationId"
                name="selectedStationId"
                placeholder="Select a station..."
                panelClass="owner-station-filter-panel">
                <mat-option [value]="null">
                  Select a station...
                </mat-option>
                <mat-option
                  *ngFor="let s of reassignOptions.stations"
                  [value]="s.station_id">
                  {{ s.station_name }}
                  ({{ s.call_sign }})
                  — {{ s.owner_name }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <div class="station-assignee-preview"
              *ngIf="selectedStationOption">
              <div class="station-assignee-preview__label">
                Will notify:
              </div>
              <div class="station-assignee-preview__info">
                <strong>Owner:</strong>
                {{ selectedStationOption.owner_name }}
                <br>
                <strong>Station Owners:</strong>
                {{ selectedStationOption.station_owners_list ||
                  'No station owners assigned' }}
              </div>
            </div>
          </div>

          <div *ngIf="reassignMode === 'user'">
            <label class="form-label">
              Assign to Admin *
            </label>
            <mat-form-field appearance="outline"
              class="support-select"
              subscriptSizing="dynamic">
              <mat-select
                [(ngModel)]="selectedUserId"
                name="selectedUserId"
                placeholder="Select an admin..."
                panelClass="owner-station-filter-panel">
                <mat-option [value]="null">
                  Select an admin...
                </mat-option>
                <mat-option
                  *ngFor="let a of reassignOptions.admins"
                  [value]="a.id">
                  {{ a.firstname }} {{ a.lastname }}
                  ({{ a.email }})
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <div class="reassign-error"
            *ngIf="reassignError">
            {{ reassignError }}
          </div>
          <div class="reassign-success"
            *ngIf="reassignSuccess">
            <mat-icon>check_circle</mat-icon>
            {{ reassignSuccess }}
          </div>

          <div class="reassign-panel__actions">
            <button class="btn-secondary"
              type="button"
              (click)="closeReassignPanel()"
              [disabled]="isReassigning">
              Cancel
            </button>
            <button class="btn-primary"
              type="button"
              (click)="confirmReassign()"
              [disabled]="isReassigning || (!selectedStationId && !selectedUserId)">
              <span class="btn-spinner"
                *ngIf="isReassigning"></span>
              {{ isReassigning ? 'Reassigning...'
                : 'Confirm Reassign' }}
            </button>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- Original description -->
    <div class="ticket-description-card">
      <div class="ticket-description-card__author">
        <div class="avatar-circle">
          {{ getInitials(ticket.firstname, ticket.lastname) }}
        </div>
        <div>
          <div class="author-name-row">
            <strong>{{ ticket.firstname }}
              {{ ticket.lastname }}</strong>
            <span
              class="source-flag"
              [class.source-flag--advertiser]="ticketSource === 'advertiser'"
              [class.source-flag--station]="ticketSource === 'station'">
              {{ ticketSourceLabel }}
            </span>
          </div>
          <span class="author-email">
            {{ ticket.user_email }}
          </span>
        </div>
        <span class="reply-time">
          {{ ticket.created_at | date:'MMM d, y' }}
        </span>
      </div>
      <div class="ticket-description-body">
        {{ ticket.description }}
      </div>
      <div class="reply-attachments"
        *ngIf="(ticket.attachments?.length ?? 0) > 0">
        <div class="reply-attachment-item"
          *ngFor="let att of ticket.attachments">
          <a [href]="att.file_url"
            target="_blank"
            rel="noopener noreferrer"
            class="attachment-preview-link">
            <img *ngIf="isImage(att.file_type)"
              [src]="att.file_url"
              [alt]="att.file_name"
              class="attachment-preview-img">
            <div *ngIf="!isImage(att.file_type)"
              class="attachment-file-link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              {{ att.file_name }}
              <small>{{ formatFileSize(att.file_size) }}</small>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- Replies thread -->
    <div class="replies-thread">
      <h3 class="replies-title"
        *ngIf="replies.length > 0">
        Conversation ({{ replies.length }})
      </h3>

      <div class="reply-item"
        *ngFor="let reply of replies"
        [class.reply-item--staff]="reply.role !== 'user'">

        <div class="reply-item__header">
          <div class="avatar-circle"
            [class.avatar-circle--staff]="reply.role !== 'user'">
            {{ getInitials(reply.firstname, reply.lastname) }}
          </div>
          <div class="reply-author">
            <strong>{{ reply.firstname }}
              {{ reply.lastname }}</strong>
            <span class="reply-role-badge"
              *ngIf="reply.role !== 'user'">
              Support Team
            </span>
          </div>
          <span class="reply-time">
            {{ reply.created_at | date:'MMM d, y h:mm a' }}
          </span>
        </div>

        <div class="reply-body">
          {{ reply.message }}
        </div>
        <div class="reply-attachments"
          *ngIf="(reply.attachments?.length ?? 0) > 0">
          <div class="reply-attachment-item"
            *ngFor="let att of reply.attachments">
            <a [href]="att.file_url"
              target="_blank"
              rel="noopener noreferrer"
              class="attachment-preview-link">
              <img *ngIf="isImage(att.file_type)"
                [src]="att.file_url"
                [alt]="att.file_name"
                class="attachment-preview-img">
              <div *ngIf="!isImage(att.file_type)"
                class="attachment-file-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                {{ att.file_name }}
                <small>{{ formatFileSize(att.file_size) }}</small>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>

    <p class="help-form-error" *ngIf="actionError">
      {{ actionError }}
    </p>

    <!-- Reply form -->
    <div class="reply-form"
      *ngIf="ticket.status !== 'closed'">
      <h3 class="reply-form__title">
        Add a Reply
      </h3>
      <textarea
        class="form-input form-textarea"
        [(ngModel)]="replyText"
        name="replyText"
        rows="4"
        placeholder="Type your reply here...">
      </textarea>

      <div class="attachment-section">
        <label class="attachment-label">
          Attachments
          <span class="attachment-hint">
            (optional — max 5 files, 10MB each)
          </span>
        </label>

        <div class="attachment-dropzone"
          (dragover)="$event.preventDefault()"
          (drop)="onAttachmentDrop($event)"
          [class.attachment-dropzone--uploading]="uploadingReplyFiles">

          <input type="file"
            id="replyAttachmentInput"
            multiple
            accept="image/*,.pdf,.txt"
            style="display:none"
            (change)="onAttachmentSelected($event)">

          <label for="replyAttachmentInput"
            class="attachment-dropzone__label">
            <svg width="20" height="20"
              viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
            <span *ngIf="!uploadingReplyFiles">
              Click or drag files to attach
            </span>
            <span *ngIf="uploadingReplyFiles">
              Uploading...
              <span class="btn-spinner-dark"></span>
            </span>
          </label>
        </div>

        <div class="attachment-list"
          *ngIf="replyAttachments.length > 0">
          <div class="attachment-item"
            *ngFor="let att of replyAttachments">

            <div class="attachment-item__thumb">
              <img
                *ngIf="isImage(att.file_type)"
                [src]="att.file_url"
                [alt]="att.file_name">
              <svg *ngIf="!isImage(att.file_type)"
                viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>

            <div class="attachment-item__info">
              <span class="attachment-item__name">
                {{ att.file_name }}
              </span>
              <span class="attachment-item__size">
                {{ formatFileSize(att.file_size) }}
              </span>
            </div>

            <button type="button"
              class="attachment-item__remove"
              (click)="removeAttachment(att.id)"
              title="Remove">
              <svg width="14" height="14"
                viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <p class="upload-error" *ngIf="replyUploadError">
          {{ replyUploadError }}
        </p>
      </div>

      <!-- Staff: show status update option inline -->
      <div class="reply-form__options"
        *ngIf="isStaff">
        <label class="checkbox-label">
          <input type="checkbox"
            [(ngModel)]="markResolved"
            name="markResolved"
            (change)="onMarkResolvedChange()">
          Mark as resolved when sending
        </label>
      </div>

      <div class="reply-form__actions">
        <button class="btn-primary"
          type="button"
          (click)="submitReply()"
          [disabled]="!replyText.trim() || isReplying">
          <span class="btn-spinner"
            *ngIf="isReplying"></span>
          {{ isReplying ? 'Sending...' :
            'Send Reply' }}
        </button>
      </div>
    </div>

    <!-- Closed ticket banner -->
    <div class="ticket-closed-banner"
      *ngIf="ticket.status === 'closed'">
      This ticket is closed.
      Contact support to reopen it.
    </div>
  </div>
</div>
